import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Grid, GridProps } from '@mui/material';
import Box from '@mui/material/Box';

import JsFileDownloader from 'js-file-downloader';
import { debounce, uniqBy } from 'lodash';

import { useModal } from 'mui-modal-provider';

import ChipOptions, { ChipOptionsProps } from '../../../components/ChipOptions/ChipOptions';
import { CheckboxOption } from '../../../components/CheckboxMultiSelect/CheckboxMultiSelect';
import { useConfirm } from '../../../components/Dialogs/Confirm/context/ConfirmDialog.context';
import ManageContentDialog from '../../../components/Dialogs/ManageContent/ManageContentDialog';
import InfiniteScroll from '../../../components/InfiniteScroll/InfiniteScroll';
import { ContentTable } from '../../../components/Tables/Content/ContentTable';
import { useNotificationsContext } from '../../../contexts/NotificationsContext';
import {
  ReservationPostRequirement,
  VenueListReservationsContentFilter,
  VenueReservationContentItemV2,
} from '../../../graphql/API';
import useListReservationContentQuery from '../../../hooks/reactQuery/useListReservationContentQuery';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { extractSubscriptionPlan } from '../../SubscriptionPlans/common/helper';
import Content from './Content';
import { ContentHeader, ContentTypeFilter, ContentPageView } from './ContentHeader';
import ContentSkeleton from './ContentSkeleton';
import { EmptyState } from './EmptyState';
import { PurchasePostsCard } from './PurchasePostsCard';

type OnClick = (item: VenueReservationContentItemV2) => void;
const commonGrid = (key: string): GridProps => ({ item: true, key, xs: 2.4 });

// Map ContentTypeFilter to ReservationPostRequirement array
function getRequirementsFromContentType(
  contentType: ContentTypeFilter,
): ReservationPostRequirement[] | undefined {
  switch (contentType) {
    case 'story':
      return [
        ReservationPostRequirement.postOnInstagramStories,
        ReservationPostRequirement.postOnInstagramStories2x,
        ReservationPostRequirement.postOnInstagramStories3x,
      ];
    case 'feed':
      return [ReservationPostRequirement.postOnInstagramFeed];
    case 'reel':
      return [ReservationPostRequirement.postOnInstagramReels];
    case 'tiktok':
      return [ReservationPostRequirement.postOnTikTokVideo];
    default:
      return undefined;
  }
}

const ListContent: React.FC = () => {
  const { showModal } = useModal();
  const { confirm } = useConfirm();

  const navigate = useNavigate();
  const { id: contentId } = useParams<{ id?: string }>();

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isFreeVenue =
    (venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans) &&
      (!subscriptionPlan || subscriptionPlan === 'basic')) ||
    ((venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)) &&
      (!subscriptionPlan || subscriptionPlan === 'basic'));

  const { handleUpdateContent } = useNotificationsContext() || {};

  const [filters, setFilters] = React.useState<
    Partial<VenueListReservationsContentFilter> | undefined
  >({
    byVenue: false,
    isPost: true,
  });

  const [view, setView] = React.useState<ContentPageView>('grid');
  const [contentType, setContentType] = React.useState<ContentTypeFilter>('all');
  const [searchText, setSearchText] = React.useState('');

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useListReservationContentQuery(filters, 50, filters?.isPost ? !isFreeVenue : true);

  const offerOptionsFromData: CheckboxOption[] = React.useMemo(() => {
    const withOffer = data.filter((x) => x.offerID != null);
    const uniqOffers = uniqBy(withOffer, (x) => x.offerID);
    return uniqOffers
      .map((x) => {
        const count = data.filter((d) => d.offerID === x.offerID).length;
        const title = (x.offerTitle ?? '').trim() || `Offer ${x.offerID}`;
        return { label: `${title} (${count})`, value: Number(x.offerID), count };
      })
      .sort((a, b) => b.count - a.count)
      .map(({ label, value }) => ({ label, value }));
  }, [data]);

  const selectedOfferIds = filters?.offerIDs ?? [];

  const allOfferOptionsRef = React.useRef<CheckboxOption[]>([]);
  if (selectedOfferIds.length === 0 && offerOptionsFromData.length > 0) {
    allOfferOptionsRef.current = offerOptionsFromData;
  }

  const offerOptions: CheckboxOption[] = React.useMemo(() => {
    const base =
      selectedOfferIds.length > 0 && allOfferOptionsRef.current.length > 0
        ? allOfferOptionsRef.current
        : offerOptionsFromData;
    const missingIds = selectedOfferIds.filter(
      (id): id is number =>
        id != null && !base.some((o) => Number(o.value) === id),
    );
    if (missingIds.length === 0) return base;
    return [
      ...base,
      ...missingIds.map((id) => ({ label: `Offer ${id}`, value: id })),
    ];
  }, [offerOptionsFromData, selectedOfferIds]);

  const selectedOffers: CheckboxOption[] = React.useMemo(() => {
    if (selectedOfferIds.length === 0) return [];
    return offerOptions.filter((opt) => selectedOfferIds.includes(Number(opt.value)));
  }, [offerOptions, selectedOfferIds]);

  const handleOffersChange = React.useCallback(
    (value: CheckboxOption | CheckboxOption[] | null) => {
      const arr = Array.isArray(value) ? value : value ? [value] : [];
      const offerIDs = arr.length > 0 ? arr.map((o) => Number(o.value)) : undefined;
      setFilters((prev) => ({ ...prev, offerIDs }));
    },
    [],
  );

  const handleContentTypeChange = React.useCallback(
    (newContentType: ContentTypeFilter) => {
      setContentType(newContentType);
      const requirements = getRequirementsFromContentType(newContentType);
      setFilters((prev) => ({ ...prev, requirements }));
    },
    [],
  );

  const debouncedSetSearchFilter = React.useMemo(
    () =>
      debounce((value: string) => {
        const search = value.trim() || undefined;
        setFilters((prev) => ({ ...prev, search }));
      }, 500),
    [],
  );

  React.useEffect(() => {
    return () => {
      debouncedSetSearchFilter.cancel();
    };
  }, [debouncedSetSearchFilter]);

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchText(value);
      debouncedSetSearchFilter(value);
    },
    [debouncedSetSearchFilter],
  );

  React.useEffect(() => {
    if (data.length > 0 && handleUpdateContent) {
      handleUpdateContent(data[0].createdAt || '');
    }
  }, [data]);

  React.useEffect(() => {
    if (contentId && data.length > 0 && !isLoading) {
      const matchingItem = data.find((item) => String(item.id) === contentId);
      if (matchingItem) {
        showModal(ManageContentDialog, {
          items: data,
          itemId: matchingItem.id ?? 0,
          confirm,
        });
      }
    }
  }, [contentId, data, isLoading, showModal, confirm]);

  const handleChipChanged = React.useCallback<ChipOptionsProps['onChange']>(
    ({ value: v }) => {
      const downloaded = v === 'purchased';
      setFilters((x) => ({
        ...x,
        byVenue: downloaded,
        ...(!downloaded ? { isPost: v === 'is-post' } : { isPost: undefined }),
      }));
    },
    [setFilters],
  );

  const handleClick = React.useCallback<OnClick>(
    (item) => {
      showModal(ManageContentDialog, {
        items: data,
        itemId: item.id ?? 0,
        confirm,
      });
    },
    [showModal, confirm, data],
  );

  const contentTab: 'posts' | 'extra-content' | 'purchased' = React.useMemo(() => {
    if (filters?.byVenue) return 'purchased';
    if (filters?.isPost && !filters?.byVenue) return 'posts';
    return 'extra-content';
  }, [filters?.isPost, filters?.byVenue]);

  const handleDownloadClick = React.useCallback(
    async (item: VenueReservationContentItemV2) => {
      const url = item.images?.video ?? item.images?.large;
      if (url) await new JsFileDownloader({ url });
    },
    [],
  );

  const renderContent = React.useCallback(() => {
    if (isLoading) {
      return [...Array(8)].map((x, i) => (
        <Grid {...commonGrid(`grid-skeleton-${i}`)}>
          <ContentSkeleton />
        </Grid>
      ));
    }

    if (data.length === 0) {
      return (
        <Grid {...commonGrid(`grid-empty`)} xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 502px)',
            }}
          >
            <EmptyState
              type={
                filters?.isPost && !filters?.byVenue
                  ? 'post'
                  : !filters?.byVenue && !filters?.isPost
                    ? 'extra-content'
                    : filters.byVenue
                      ? 'purchased'
                      : 'post'
              }
              isBrand={venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)}
              productHandle={() => {
                if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)) {
                  navigate('/products/create');
                } else {
                  navigate('/offers/create');
                }
              }}
              inviteHandle={() => {
                navigate('/collabs');
              }}
              buyExtraContentHandle={() => {
                setFilters((prev) => ({ ...prev, isPost: false, byVenue: false }));
              }}
            />
          </Box>
        </Grid>
      );
    }

    if (view === 'list') {
      return null;
    }

    return data.map((x) => (
      <Grid {...commonGrid(`grid-content-${x.id}`)}>
        <Content
          onClick={() => handleClick(x)}
          src={x.images?.medium ?? ''}
          video={x.images?.video ?? undefined}
          userAvatar={x.user?.picture?.thumbnail ?? ''}
          name={`${x.user?.firstname ?? ''} ${x.user?.lastname ?? ''}`.trim() || '—'}
          offerTitle={x.offerTitle ?? undefined}
          data={x}
        />
      </Grid>
    ));
  }, [
    isLoading,
    data,
    isFreeVenue,
    filters?.isPost,
    filters?.byVenue,
    venueState?.tags,
    navigate,
    handleClick,
    view,
  ]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0, justifyContent: 'space-between' }}>
        <ChipOptions
          defaultValue={
            filters?.isPost && !filters?.byVenue
              ? 'is-post'
              : !filters?.byVenue && !filters?.isPost
                ? 'not-post'
                : filters?.byVenue
                  ? 'purchased'
                  : 'is-post'
          }
          name='contentDownloaded'
          options={[
            { label: 'Posts', value: 'is-post' },
            { label: 'Extra content', value: 'not-post' },
            { label: 'Purchased', value: 'purchased' },
          ]}
          onChange={handleChipChanged}
        />
        <ContentHeader
          contentType={contentType}
          onContentTypeChange={handleContentTypeChange}
          offerOptions={offerOptions}
          selectedOffers={selectedOffers}
          onOffersChange={handleOffersChange}
          searchText={searchText}
          onSearchChange={handleSearchChange}
          view={view}
          onViewChange={setView}
          showPlatformFilters={contentTab === 'posts'}
        />
      </Box>

      <Box sx={{ pt: 4 }}>
        {view === 'list' ? (
          !isLoading && data.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 'calc(100vh - 502px)',
              }}
            >
              <EmptyState
                type={
                  filters?.isPost && !filters?.byVenue
                    ? 'post'
                    : !filters?.byVenue && !filters?.isPost
                      ? 'extra-content'
                      : filters?.byVenue
                        ? 'purchased'
                        : 'post'
                }
                isBrand={venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)}
                productHandle={() => {
                  if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)) {
                    navigate('/products/create');
                  } else {
                    navigate('/offers/create');
                  }
                }}
                inviteHandle={() => {
                  navigate('/collabs');
                }}
                buyExtraContentHandle={() => {
                  setFilters((prev) => ({ ...prev, isPost: false, byVenue: false }));
                }}
              />
            </Box>
          ) : (
            <ContentTable
              data={data}
              contentTab={contentTab}
              isLoading={isLoading}
              isFetching={isFetching}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              onRowClick={handleClick}
              onDownloadClick={handleDownloadClick}
            />
          )
        ) : (
          <InfiniteScroll
            isLoading={isLoading}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          >
            <Grid container spacing={2}>
              {renderContent()}
            </Grid>
          </InfiniteScroll>
        )}
      </Box>
    </Box>
  );
};

export default ListContent;
